/**
 * QMS Filter Bar - Workflow filtering and search
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, X, Search, User, ChevronDown, Building2, Check, ChevronsUpDown } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { QmsWorkflowFilter } from '../../types/qms.types';
import type { QmsTypeFilter } from '../../hooks/useQmsFilters';
import type { Account } from '@/features/accounts/types';
import type { OwnerOption } from '@/features/rocks/api';
import type { Team } from '@/features/rocks/types';

interface QmsFilterBarProps {
  typeFilter: QmsTypeFilter;
  onTypeFilterChange: (filter: QmsTypeFilter) => void;
  workflowFilter: QmsWorkflowFilter;
  onFilterChange: (filter: QmsWorkflowFilter) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  assigneeFilter: string;
  onAssigneeChange: (assigneeId: string) => void;
  assignees: OwnerOption[];
  accountFilter: string;
  onAccountChange: (accountId: string) => void;
  accounts: Account[];
  departmentFilter: string;
  onDepartmentChange: (department: string) => void;
  departments: Team[];
  pendingQdCount: number;
  pendingCeoCount: number;
}

export function QmsFilterBar({
  typeFilter,
  onTypeFilterChange,
  workflowFilter,
  onFilterChange,
  searchQuery,
  onSearchChange,
  assigneeFilter,
  onAssigneeChange,
  assignees,
  accountFilter,
  onAccountChange,
  accounts,
  departmentFilter,
  onDepartmentChange,
  departments,
  pendingQdCount,
  pendingCeoCount,
}: QmsFilterBarProps) {
  const { t } = useTranslation('qms');
  const [accountComboboxOpen, setAccountComboboxOpen] = useState(false);

  const filterOptions: Array<{ value: QmsWorkflowFilter; label: string }> = [
    { value: 'all', label: t('filters.all') },
    { value: 'pending_qd_approval', label: t('filters.pending_qd_approval') },
    { value: 'qd_approved', label: t('filters.qd_approved') },
    { value: 'pending_ceo_review', label: t('filters.pending_ceo_review') },
    { value: 'ceo_confirmed', label: t('filters.ceo_confirmed') },
  ];

  const typeFilterOptions: Array<{ value: QmsTypeFilter; label: string }> = [
    { value: 'all', label: t('filters.typeAll') },
    { value: 'ticket', label: t('filters.typeTicket') },
    { value: 'capa', label: t('filters.typeCapa') },
    { value: 'client', label: t('filters.typeClient') },
  ];

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-3 space-y-3">
      {/* Type filter tabs */}
      <div className="flex items-center gap-2">
        {typeFilterOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => onTypeFilterChange(option.value)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              typeFilter === option.value
                ? 'bg-atek-blue text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-4 flex-wrap flex-1">
        {/* Search input */}
        <div className="relative flex-1 min-w-[300px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('search.placeholder')}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue focus:border-transparent"
          />
          {/* Clear button */}
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Assignee filter dropdown */}
        <div className="relative min-w-[180px]">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none z-10" />
          <select
            value={assigneeFilter}
            onChange={(e) => onAssigneeChange(e.target.value)}
            aria-label={t('filters.assigneeLabel')}
            className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue focus:border-transparent appearance-none bg-white cursor-pointer"
          >
            <option value="all">{t('filters.allAssignees')}</option>
            <option value="unassigned">{t('filters.unassigned')}</option>
            {assignees.map((owner) => (
              <option key={owner.id} value={owner.mongoUserId || owner.id}>
                {owner.name}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        </div>

        {/* Department filter dropdown */}
        <div className="relative min-w-[180px]">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none z-10" />
          <select
            value={departmentFilter}
            onChange={(e) => onDepartmentChange(e.target.value)}
            aria-label={t('filters.departmentLabel')}
            className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue focus:border-transparent appearance-none bg-white cursor-pointer"
          >
            <option value="all">{t('filters.allDepartments')}</option>
            {departments.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        </div>

        {/* Account filter dropdown - Searchable Combobox */}
        <Popover open={accountComboboxOpen} onOpenChange={setAccountComboboxOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={accountComboboxOpen}
              aria-label={t('filters.accountLabel')}
              className="min-w-[200px] justify-between font-normal border-gray-300"
            >
              <Building2 className="mr-2 h-4 w-4 text-gray-400 shrink-0" />
              <span className="truncate">
                {accountFilter === 'all'
                  ? t('filters.allAccounts')
                  : accountFilter === 'unassigned'
                    ? t('filters.noAccount')
                    : accounts.find((a) => a.id === accountFilter)?.name || accountFilter}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0" align="start">
            <Command>
              <CommandInput placeholder={t('filters.searchAccount', 'Search account...')} />
              <CommandList>
                <CommandEmpty>{t('filters.noAccountFound', 'No account found.')}</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value="all"
                    onSelect={() => {
                      onAccountChange('all');
                      setAccountComboboxOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        accountFilter === 'all' ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {t('filters.allAccounts')}
                  </CommandItem>
                  <CommandItem
                    value="unassigned"
                    onSelect={() => {
                      onAccountChange('unassigned');
                      setAccountComboboxOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        accountFilter === 'unassigned' ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {t('filters.noAccount')}
                  </CommandItem>
                  {accounts.map((account) => (
                    <CommandItem
                      key={account.id}
                      value={account.name}
                      onSelect={() => {
                        onAccountChange(account.id);
                        setAccountComboboxOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          accountFilter === account.id ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      {account.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <div className="flex items-center gap-4">
        {/* Filter label */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Filter className="h-4 w-4" />
          <span className="font-medium">{t('filters.label')}</span>
        </div>

        {/* Filter buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          {filterOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => onFilterChange(option.value)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                workflowFilter === option.value
                  ? 'bg-atek-blue text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {option.label}
              {option.value === 'pending_qd_approval' && pendingQdCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-yellow-500 text-white text-xs">
                  {pendingQdCount}
                </span>
              )}
              {option.value === 'pending_ceo_review' && pendingCeoCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-purple-500 text-white text-xs">
                  {pendingCeoCount}
                </span>
              )}
            </button>
          ))}

          {/* Clear filter */}
          {(workflowFilter !== 'all' || assigneeFilter !== 'all' || accountFilter !== 'all' || departmentFilter !== 'all') && (
            <button
              onClick={() => {
                onFilterChange('all');
                onAssigneeChange('all');
                onAccountChange('all');
                onDepartmentChange('all');
              }}
              className="flex items-center gap-1 px-2 py-1.5 rounded-md text-sm text-gray-600 hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
              {t('filters.clear')}
            </button>
          )}
        </div>
        </div>
      </div>

      </div>
    </div>
  );
}
